import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowRight, MessageSquare, Award, Sparkles, Target, Play, ChevronDown } from "lucide-react";
import { useAuth } from '@/hooks/useAuth';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import MarketingNav from '@/components/marketing/MarketingNav';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import HowItWorks from '@/components/marketing/HowItWorks';
import FeaturesShowcase from '@/components/marketing/FeaturesShowcase';
import Testimonials from '@/components/marketing/Testimonials';
import FAQ from '@/components/marketing/FAQ';
import CTASection from '@/components/marketing/CTASection';
import AppShowcase from '@/components/marketing/AppShowcase';
import RoleplayShowcase from '@/components/marketing/RoleplayShowcase';
import FeedbackShowcase from '@/components/marketing/FeedbackShowcase';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import Seo from '@/components/Seo';

const landingFaqs = [
  { q: 'What is BigE Sales Coach?', a: 'BigE Sales Coach is an AI-powered sales training platform designed for collegiate sales competition preparation, with realistic AI buyer roleplay and instant rubric-based feedback.' },
  { q: 'How does the AI buyer work?', a: 'AI buyers are trained on real sales competition scripts and respond dynamically to your questions, simulating realistic objections and natural conversation. You can practice via voice or text.' },
  { q: 'What scoring criteria do you use?', a: 'We use the standard collegiate sales competition rubric with 7 categories: Open, Needs Identification, Solution Presentation, Overcoming Objections, Close, Communication, and Overall Performance.' },
  { q: 'Can I practice with voice?', a: 'Yes — BigE supports full voice conversation mode using advanced speech-to-text, and the AI buyer responds with synthesized voice.' },
  
  { q: 'What competitions does BigE prepare me for?', a: 'BigE prepares you for major collegiate sales competitions including NCSC, ICSC, and regional events.' },
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: landingFaqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scrollY, setScrollY] = useState(0);
  const isMobile = useIsMobile();
  const rafRef = useRef<number>();

  // Throttled scroll handler using RAF - disabled on mobile for performance
  useEffect(() => {
    if (isMobile) return; // Disable parallax on mobile
    
    const handleScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        setScrollY(window.scrollY);
        rafRef.current = undefined;
      });
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isMobile]);

  const handleStart = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/auth');
    }
  };

  return (
    <div className="h-screen bg-gradient-dark text-foreground overflow-y-auto overflow-x-hidden snap-y snap-mandatory scroll-smooth">
      <Seo
        title="BigE | AI Sales Roleplay & Competition Prep"
        description="Practice AI sales roleplays with instant rubric feedback. Built for competitions, programs, and enterprise teams."
        path="/"
        jsonLd={faqJsonLd}
      />
      {/* Animated background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-grid opacity-[0.02]" />
        
        {/* Floating orbs with parallax - reduced blur and no animation on mobile */}
        <div 
          className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-primary/20 rounded-full blur-[50px] md:blur-[150px] md:animate-pulse-glow will-change-transform"
          style={!isMobile ? { transform: `translateY(${scrollY * 0.1}px)` } : undefined}
        />
        <div 
          className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-accent/20 rounded-full blur-[50px] md:blur-[150px] md:animate-pulse-glow will-change-transform"
          style={!isMobile ? { 
            animationDelay: '1s',
            transform: `translateY(${scrollY * -0.08}px)`
          } : undefined}
        />
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-electric-purple/10 rounded-full blur-[50px] md:blur-[150px] will-change-transform"
          style={!isMobile ? { transform: `translate(-50%, -50%) scale(${1 + scrollY * 0.0002})` } : undefined}
        />
        
        {/* Grid lines effect */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:100px_100px]" />
      </div>

      <MarketingNav />

      <main>
      {/* Hero Section - Enhanced */}
      <section className="relative pt-32 sm:pt-40 pb-20 sm:pb-32 min-h-screen flex items-center snap-start">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center justify-center text-center max-w-5xl mx-auto">
            <div className="space-y-6 sm:space-y-8 w-full">
              {/* Animated badge */}
              <div 
                className="inline-flex items-center gap-1.5 sm:gap-2 px-4 py-2 rounded-full glass animate-fade-in border border-primary/20" 
                style={{ animationDelay: '0.1s' }}
              >
                <div className="relative">
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  <div className="absolute inset-0 animate-ping">
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary opacity-50" />
                  </div>
                </div>
                <span className="text-xs sm:text-sm font-medium text-foreground">Objective Intelligence Sales Training</span>
              </div>

              {/* Main title with animated gradient */}
              <h1 
                className="font-display text-5xl sm:text-7xl lg:text-8xl font-bold tracking-tight animate-fade-in leading-[0.95]" 
                style={{ animationDelay: '0.2s' }}
              >
                <span className="block mb-2">Crush Your</span>
                <span className="text-gradient bg-gradient-to-r from-primary via-accent to-electric-purple bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]">
                  Sales Competition
                </span>
              </h1>

              {/* Subtitle with visual hierarchy */}
              <p 
                className="text-lg sm:text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed animate-fade-in px-4 sm:px-2" 
                style={{ animationDelay: '0.4s' }}
              >
                <span className="block">
                  AI-powered sales practice with <span className="text-primary font-semibold">feedback in seconds</span>.
                </span>
              </p>

              {/* CTA Buttons with hover effects */}
              <div 
                className="pt-4 sm:pt-6 animate-fade-in flex flex-col sm:flex-row gap-4 justify-center items-center" 
                style={{ animationDelay: '0.5s' }}
              >
                <Button 
                  variant="glass" 
                  size="lg" 
                  onClick={handleStart}
                  aria-label="Start practicing sales for free"
                  className="group text-base sm:text-lg px-8 py-6 bg-primary/20 hover:bg-primary/30 text-primary border-primary/30 hover:border-primary/50 shadow-lg shadow-primary/10 hover:shadow-primary/20"
                >
                  <Play className="w-5 h-5" />
                  Start Practicing
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button 
                  variant="glass" 
                  size="lg" 
                  onClick={() => navigate('/contact')}
                  aria-label="Book a demo"
                  className="text-base sm:text-lg px-8 py-6 group shadow-none"
                >
                  Book a Demo
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>

            </div>

            {/* Feature Cards with stagger animation */}
            <div 
              className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-16 sm:mt-24 w-full px-2 sm:px-0" 
            >
              <FeatureCard 
                icon={<MessageSquare className="w-6 h-6" />}
                title="AI Buyers That See You"
                description="Buyers respond to your words, tone, expressions, and body language — pushing back on weak discovery and rewarding strong questions"
                glowColor="primary"
                delay={0.7}
              />
              <FeatureCard 
                icon={<Target className="w-6 h-6" />}
                title="Multi-Category Scoring"
                description="Get scored on Open, Needs, Value, Objections, Close, Communication & Impact — with SPIN and CAC framework breakdowns"
                glowColor="accent"
                delay={0.8}
              />
              <FeatureCard 
                icon={<Award className="w-6 h-6" />}
                title="Visual Progress Analytics"
                description="Track improvement with trend charts, category breakdowns, streak tracking, and AI-generated coaching recommendations"
                glowColor="purple"
                delay={0.9}
              />
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-6 h-6 text-muted-foreground" />
        </div>
      </section>


      <div className="snap-start">
        <AppShowcase />
      </div>

      <div className="snap-start">
        <HowItWorks />
      </div>

      <div className="snap-start">
        <RoleplayShowcase />
      </div>

      <div className="snap-start">
        <FeedbackShowcase />
      </div>

      <div className="snap-start">
        <FeaturesShowcase />
      </div>

      <div className="snap-start">
        {/* <Testimonials /> */}
      </div>

      <div className="snap-start">
        <FAQ />
      </div>

      <div className="snap-start">
        <CTASection />
      </div>
      </main>

      <div className="snap-start">
        <MarketingFooter />
      </div>
    </div>
  );
};

const FeatureCard = ({ 
  icon, 
  title, 
  description,
  glowColor,
  delay = 0,
}: { 
  icon: React.ReactNode; 
  title: string; 
  description: string;
  glowColor: 'primary' | 'accent' | 'purple';
  delay?: number;
}) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  const glowClasses = {
    primary: 'group-hover:shadow-[0_0_40px_-5px_hsl(199_89%_48%/0.4)] group-hover:border-primary/30',
    accent: 'group-hover:shadow-[0_0_40px_-5px_hsl(185_85%_50%/0.4)] group-hover:border-accent/30',
    purple: 'group-hover:shadow-[0_0_40px_-5px_hsl(260_70%_60%/0.4)] group-hover:border-electric-purple/30',
  };

  const iconGlowClasses = {
    primary: 'text-primary bg-primary/10 group-hover:bg-primary/20 group-hover:scale-110',
    accent: 'text-accent bg-accent/10 group-hover:bg-accent/20 group-hover:scale-110',
    purple: 'text-electric-purple bg-electric-purple/10 group-hover:bg-electric-purple/20 group-hover:scale-110',
  };

  return (
    <div 
      ref={ref}
      className={`group p-5 sm:p-6 rounded-xl sm:rounded-2xl glass border border-white/5 ${glowClasses[glowColor]} transition-all duration-500 text-center cursor-default ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}s` }}
    >
      <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 transition-all duration-300 ${iconGlowClasses[glowColor]} mx-auto`}>
        {icon}
      </div>
      <p className="font-display font-semibold text-lg sm:text-xl mb-2 text-foreground" role="heading" aria-level={3}>{title}</p>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
};

export default Landing;
